<template>
  <q-page padding>
    <AuthenticateLayout withHeader>
      <template v-slot:title>{{ t('activate_account') }}</template>
      <template v-slot:heading>
        <div class="row no-wrap items-center">
          <div class="col">
            {{ t('activate_username_create_new') }}
          </div>
          <div class="col-auto">
            <zbm-btn-icon @click="showCustomerSupportInfoDialog = true"
                          icon="fa-light fa-circle-question"
                          size="30px"
                          data-testid="help" />
          </div>
        </div>
      </template>

      <template v-slot:default>
        <UsernameConfirmWithValidation ref="usernameComponent" v-model="usernameResult" :initialLabel="''"
          :initialPlaceholder="'activate_username_create'" :confirmationLabel="''"
          :confirmationPlaceholder="'activate_username_confirm'" @validate="validateResult" />
      </template>

      <template v-slot:actions>
        <ProgressBar :backEnabled="false" :progress="78" v-model:nextEnabled="formIsValid" @on-next-click="onNextClick" />
      </template>
    </AuthenticateLayout>

    <!-- Help Dialog -->
    <zbm-dialog v-model="showCustomerSupportInfoDialog">
      <!-- Header -->
      <div class="row q-dialog__header">
        <div class="col-auto">
          <!-- Help Icon -->
          <q-icon color="dark-teal"
                  name="fa-solid fa-circle-question"
                  size="42px" />
        </div>
      </div>

      <!-- Body -->
      <div class="row q-dialog__body">
          <div class="col">
              <h3 class="text-weight-semibold q-mb-lg">{{ t('sign_up_help') }}</h3>
              <div class="support-info-text" v-html="t('activate_auth_help_mymobility_phone_email_pwe', [
                sessionStore?.supportInfo?.countrySupportPhone,
                sessionStore?.supportInfo?.countrySupportEmail,
                sessionStore?.supportInfo?.patientSupportUrl,
                supportUrlOrProfileInfo])">
              </div>
          </div>
      </div>

      <!-- Footer (CTA button(s)) -->
      <div class="row q-dialog__footer">
        <div class="col-auto">
          <zbm-btn v-close-popup
                    :color="$zb.enums.ButtonStdColorEnum.accentText"
                    :height="$zb.enums.ButtonHeightEnum.lg"
                    :label="t('gen_okay')"
                    data-testid="okay"/>
        </div>
      </div>
    </zbm-dialog>
  </q-page>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import AuthenticateLayout from 'src/components/layouts/AuthenticateLayout.vue';
import ProgressBar from 'src/components/ProgressBar.vue';
import { createUserName, getRegistrationNextStepSkipLoginWithExting } from 'src/api/endpoints/registrationWeb';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { translateStepToRoute } from 'src/utils/interfaceUtils';
import { useActivationStore, useSessionStore } from 'src/store';
import UsernameConfirmWithValidation from 'src/components/form/UsernameConfirmWithValidation.vue';
import { type UsernameConfirmWithValidationResult, makeEmptyUsernameConfirmWithValidationResult } from 'src/utils/componentFactories';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';

const $q = useQuasar(),
  router = useRouter(),
  {t} = useI18n(),
  activationStore = useActivationStore(),
  sessionStore = useSessionStore(),
  usernameComponent = ref(null),
  usernameResult = ref(makeEmptyUsernameConfirmWithValidationResult()),
  showCustomerSupportInfoDialog = ref(false),
  formIsValid = computed(() => usernameResult.value.isValid),
  supportUrlOrProfileInfo = computed(() =>  t('activate_auth_help_click_here_pwe'));

onMounted(() => {
  (document.querySelectorAll('input:first-child')[0] as HTMLElement).focus();
});

async function onNextClick() {
  if (!usernameResult.value.isValid) return;

  $q.loading.show()
  const userNameResponse = await handleApiRequest(() =>
    createUserName(usernameResult.value.username), null, $q
  );

  if (userNameResponse?.result) {
    activationStore.setRegistrationUsername(usernameResult.value.username);

    const step = await handleApiRequest(() => getRegistrationNextStepSkipLoginWithExting(), null, $q);
    const nextStep = translateStepToRoute(step?.result as string);

    $q.loading.hide()
    router.push({
      name: nextStep,
    });
  } else {
    $q.loading.hide()
  }
}

async function validateResult(result: UsernameConfirmWithValidationResult) {
  usernameResult.value.isValid = result.isValid;
  usernameResult.value.username = result.username;
  usernameResult.value.confirmUsername = result.confirmUsername;
}
</script>

<style lang="scss" scoped>
    .support-info-text {
        word-wrap: break-word;
    }
</style>